import React, { useState, useEffect } from 'react'
import {
  Box, Grid, Alert, Typography, Button, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useNavigate } from 'react-router-dom'
import _ from 'lodash'
import { LoadingButton } from '@mui/lab'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import withLayout from '../../hocs/Layout'
import PageTitle from '../../common/PageTitle'
import { FormProvider, RHFTextField, RHFSelectField } from '../../common/hook-form'
import { apiConstants, postGeneric } from '../../common/constants'
import { downloadCSV, mergeServicability } from '../../helpers/UtilityHelper'
import { getMerchant } from '../../helpers/ReduxHelpers'
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography'
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS } from '../../styles/foundation/colors'
import { SHADOW } from '../../styles/foundation/effects'
import { pageContainerSx } from '../../styles/patterns'

const serviceTypes = [
  { label: 'Forward', value: 'FORWARD' },
  { label: 'Reverse', value: 'RVP' },
]

function AvailChip({ ok }) {
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.35, borderRadius: '999px',
      bgcolor: ok ? `${SUCCESS}14` : '#9e9e9e14', color: ok ? SUCCESS : SUBTLE,
      fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
    }}>
      {ok ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CancelIcon sx={{ fontSize: 14 }} />}
      {ok ? 'Available' : 'Not available'}
    </Box>
  )
}

function CheckServiceability() {
  const merchant = getMerchant()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [couriers, setCouriers] = useState(null)

  const methods = useForm({
    resolver: yupResolver(Yup.object().shape({
      pickupPincode: Yup.string().required('Required').matches(/^[1-9][0-9]{5}$/, 'Enter a valid 6-digit PIN'),
      deliveryPincode: Yup.string().required('Required').matches(/^[1-9][0-9]{5}$/, 'Enter a valid 6-digit PIN'),
      serviceType: Yup.string().required('Required'),
    })),
    defaultValues: { pickupPincode: '', deliveryPincode: '', serviceType: 'FORWARD' },
  })

  // Reverse (RVP) is prepaid-only — hide the COD column for it. Clear stale
  // results whenever the service type changes so the table re-checks.
  const serviceType = methods.watch('serviceType')
  const isForward = serviceType === 'FORWARD'
  useEffect(() => { setCouriers(null) }, [serviceType])

  const onSubmit = async (d) => {
    setLoading(true)
    try {
      // 1) route serviceability  2) courier master — merged so each parent courier
      //    is shown once with its Prepaid/COD availability (same as the prod page).
      const { data: serviceabilityPincodes } = await postGeneric(
        `${apiConstants.SERVICEABILITY_PINCODE}?merchantId=${merchant?.merchantId || merchant?.id}&isAdminPanelCall=${false}`,
        [{ pickup_pincode: d.pickupPincode, drop_pincode: d.deliveryPincode, optional: { service_type: d.serviceType } }],
      )
      const { data: courierList } = await postGeneric(apiConstants.COURIER_LIST, {
        andfilter: [{ field: 'isDeleted', operator: 'eq', value: '0' }],
        offset: 0,
        limit: 1000,
      })
      const merged = serviceabilityPincodes?.result
        ? mergeServicability(serviceabilityPincodes.result, courierList)
        : []
      setCouriers(
        _.orderBy(merged, 'account_code').map((row) => ({
          name: row.account_code,
          prepaid: !!row?.serviceable?.PREPAID,
          cod: !!row?.serviceable?.COD,
        })),
      )
    } catch { setCouriers([]) }
    setLoading(false)
  }

  const downloadResults = () => {
    if (!couriers?.length) return
    downloadCSV(
      couriers.map((c) => ({
        'Courier Partner': c.name,
        Prepaid: c.prepaid ? 'Available' : 'Not Available',
        ...(isForward ? { COD: c.cod ? 'Available' : 'Not Available' } : {}),
      })),
      'serviceability-check.csv',
    )
  }

  const cardSx = { bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '14px', boxShadow: SHADOW.card, p: { xs: 2.5, md: 3 } }
  const headSx = { fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'], color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.03em' }
  const cellSx = { fontFamily: FONT, fontSize: SIZE.xs, color: NAVY }

  return (
    <Box sx={pageContainerSx}>
      <PageTitle>Serviceability Check</PageTitle>
      <Grid container spacing={2.5} alignItems='flex-start'>
        {/* Left — check form */}
        <Grid item xs={12} md={5}>
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', gap: 1.25, mb: 2.5 }}>
              <LocationOnOutlinedIcon sx={{ color: PRIMARY_BLUE, mt: 0.2 }} />
              <Box>
                <Typography sx={{ fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY }}>Check Pincode Serviceability</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE }}>Enter source and destination pincodes to check courier availability</Typography>
              </Box>
            </Box>

            <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
              <Box sx={{ mb: 2 }}><RHFTextField name='pickupPincode' label='Source Pincode' placeholder='e.g., 110001' size='medium' fullWidth /></Box>
              <Box sx={{ mb: 2 }}><RHFTextField name='deliveryPincode' label='Destination Pincode' placeholder='e.g., 110009' size='medium' fullWidth /></Box>
              <Box sx={{ mb: 2.5 }}><RHFSelectField name='serviceType' label='Service Type' options={serviceTypes} width='100%' /></Box>
              <LoadingButton loading={loading} type='submit' fullWidth variant='contained' startIcon={<SearchOutlinedIcon />}
                sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.bold, fontSize: SIZE.sm, borderRadius: '10px', py: 1.25, bgcolor: PRIMARY_BLUE, '&:hover': { bgcolor: PRIMARY_BLUE, filter: 'brightness(0.95)' } }}>
                Check Serviceability
              </LoadingButton>
            </FormProvider>

            <Divider sx={{ my: 2.5 }} />
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mb: 1.25 }}>Need comprehensive serviceability data?</Typography>
            <Button variant='outlined' startIcon={<DownloadOutlinedIcon />} onClick={() => navigate('/serviceable-pincodes/download')}
              sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, borderRadius: '999px', color: PRIMARY_BLUE, borderColor: `${PRIMARY_BLUE}66` }}>
              Download Bulk Serviceability
            </Button>
          </Box>
        </Grid>

        {/* Right — results */}
        <Grid item xs={12} md={7}>
          <Box sx={cardSx}>
            {couriers === null ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <LocalShippingOutlinedIcon sx={{ fontSize: 40, color: BORDER, mb: 1 }} />
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, color: SUBTLE }}>Enter pincodes and check to see available courier partners.</Typography>
              </Box>
            ) : couriers.length === 0 ? (
              <Alert severity='warning' sx={{ borderRadius: '10px', fontFamily: FONT, fontSize: SIZE.xs }}>
                These pincodes are not serviceable for any courier partner.
              </Alert>
            ) : (
              <>
                <Alert severity='info' sx={{ borderRadius: '10px', mb: 2.5, fontFamily: FONT, fontSize: SIZE.xs }}>
                  <strong>Great News!</strong> Pincodes are serviceable! <strong>{couriers.length}</strong> courier partner{couriers.length > 1 ? 's' : ''} {couriers.length > 1 ? 'are' : 'is'} serving these pincodes.
                </Alert>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY }}>Available Courier Partners</Typography>
                  <Button onClick={downloadResults} variant='contained' size='small' startIcon={<DownloadOutlinedIcon />}
                    sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], borderRadius: '8px', bgcolor: PRIMARY_BLUE, '&:hover': { bgcolor: PRIMARY_BLUE } }}>
                    Download CSV
                  </Button>
                </Box>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ borderBottom: `1px solid ${BORDER}` }}>
                        <TableCell sx={headSx}>Courier Partner</TableCell>
                        <TableCell sx={headSx} align='center'>Prepaid</TableCell>
                        {isForward && <TableCell sx={headSx} align='center'>COD</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {couriers.map((c) => (
                        <TableRow key={c.name} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                          <TableCell sx={{ ...cellSx, fontWeight: WEIGHT.semibold }}>
                            <LocalShippingOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mr: 0.75, verticalAlign: 'text-bottom' }} />
                            {c.name}
                          </TableCell>
                          <TableCell align='center'><AvailChip ok={c.prepaid} /></TableCell>
                          {isForward && <TableCell align='center'><AvailChip ok={c.cod} /></TableCell>}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default withLayout(CheckServiceability)
